import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ContractList from "./ContractList";
import ContractTabs from "./ContractTabs";
import SearchAndFilter from "./SearchAndFilter";
import SubscriptionModal from "./SubscriptionModal";
import ContractDetailModal from "./ContractDetailModal";
import ProductDetailsView from "./ProductDetailsView";
import { ProfilingResults } from "./ProfilingResults";
import ViewLineage from "./ViewLineage";
import { DQResult } from "../Domain/DQResult";
import { AssetDetails } from "./AssetDetails";
import "./LandingPage.scss";
import axios from "axios";
import heartLoading from "../../../assests/heartLoading.gif";
import preloader from "../../../assests/preloader1.gif";
import { toast, Toaster } from "react-hot-toast";
// import SideFilter from "./SideFilter.tsx"; // Removed side filter import

const LandingPage = () => {
  const navigate = useNavigate();   
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [subscribedTables, setSubscribedTables] = useState([]);
  const [showSubscriptionForm, setShowSubscriptionForm] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [showContractModal, setShowContractModal] = useState(false);
  const [selectedContractForView, setSelectedContractForView] = useState(null);
  const [contractDetails, setContractDetails] = useState(null);
  const [contractLoading, setContractLoading] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState([]); // Default selection

  // Product details popup state
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProductForView, setSelectedProductForView] = useState(null);
  const [productLoading, setProductLoading] = useState(false);

  // Result popup state
  const [showResultModal, setShowResultModal] = useState(false);
  const [selectedResultForView, setSelectedResultForView] = useState(null);
  const [activeResultTab, setActiveResultTab] = useState("profile"); // 'profile', 'quality', or 'remediation'

  // Lineage popup state
  const [showLineageModal, setShowLineageModal] = useState(false);
  const [selectedLineageForView, setSelectedLineageForView] = useState(null);

  // Profiling results state
  const [profilingResults, setProfilingResults] = useState(null);
  const [profilingLoading, setProfilingLoading] = useState(false);

  // DQ results state
  const [dqResults, setDqResults] = useState(null);
  const [dqLoading, setDqLoading] = useState(false);

  // Asset details popup state
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [selectedAssetForView, setSelectedAssetForView] = useState(null);
  const [assetData, setAssetData] = useState(null);
  const [assetLoading, setAssetLoading] = useState(false);

  // Filter state with IDs for hierarchical filtering
  const [parentDomain, setParentDomain] = useState("All Parent Domains");
  const [parentDomainId, setParentDomainId] = useState(null);
  const [domain, setDomain] = useState("All Domains");
  const [domainId, setDomainId] = useState(null);
  const [subDomain, setSubDomain] = useState("All Sub Domains");
  const [subDomainId, setSubDomainId] = useState(null);
  const [product, setProduct] = useState("All Products");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // API data states
  const [apiData, setApiData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false); // Loading state for search operations
  const [filterLoading, setFilterLoading] = useState(false); // Loading state for filter operations
  const [error, setError] = useState(null);
  const [productDetails, setProductDetails] = useState([]); // Store product details from edit-form API

  // Filter options from API - updated for hierarchical structure
  const [filterOptions, setFilterOptions] = useState({
    parentDomains: [],
    domains: [],
    subDomains: [],
    products: [],
  });

  // Store the full hierarchical filters data from API
  const [hierarchicalFilters, setHierarchicalFilters] = useState(null);

  const [subscriptionForm, setSubscriptionForm] = useState({
    productName: "",
    projectCode: "",
    readerGroup: "",
    usageDuration: "3months",
    usageDescription: "",
    notificationEmail: "",
    acceptTerms: false,
  });
  const [allFieldsValid, setAllFieldsValid] = useState(true);
  const [formTouched, setFormTouched] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Update allFieldsValid whenever form or errors change
  useEffect(() => {
    const isValid = 
      subscriptionForm.productName &&
      subscriptionForm.projectCode &&
      subscriptionForm.readerGroup &&
      subscriptionForm.usageDuration &&
      subscriptionForm.usageDescription &&
      subscriptionForm.notificationEmail &&
      Object.keys(formErrors).length === 0;
    setAllFieldsValid(isValid);
  }, [subscriptionForm, formErrors]);

  // Apply filters to current data (local filtering)
  const applyFilters = () => {
    let filtered = [...apiData];

    // Filter by parent domain
    if (parentDomain !== "All Parent Domains") {
      filtered = filtered.filter(
        (item) => item.parent_domain_name === parentDomain
      );
    }

    // Filter by domain
    if (domain !== "All Domains") {
      filtered = filtered.filter((item) => item.domain_name === domain);
    }

    // Filter by sub domain
    if (subDomain !== "All Sub Domains") {
      filtered = filtered.filter((item) => item.sub_domain_name === subDomain);
    }

    // Filter by product
    if (product !== "All Products") {
      filtered = filtered.filter((item) => item.product_name === product);
    }

    setFilteredData(filtered);
  };

  // Fetch data from API when component mounts
  const fetchDataDiscovery = async (searchPayload = null) => {
    // setLoading(true);
    try {
      // Process search query - convert spaces to underscores and trim
      const processedSearchQuery = searchQuery ? searchQuery.trim().replace(/\s+/g, '_') : "";
      
      const payload = searchPayload || {
        parentDomain: parentDomain === "All Parent Domains" ? "" : parentDomain,
        domain: domain === "All Domains" ? "" : domain,
        subDomain: subDomain === "All Sub Domains" ? "" : subDomain,
        product: product === "All Products" ? "" : product,
        searchValue: processedSearchQuery,
        page: currentPage,
        pageSize: pageSize,
      };

      // Add ownerName or subscribedBy based on active tab
      if (activeTab === "myContracts") {
        payload.ownerName = localStorage.getItem("userId") ||  "";
      } else if (activeTab === "mySubscriptions") {
        payload.subscribedBy = localStorage.getItem("userId") ||  "";
      }

      // Use different API endpoint based on active tab
      let apiEndpoint;
      if (activeTab === "myContracts") {
        apiEndpoint = `${process.env.REACT_APP_API_URL}/my-data-contracts-results`;
      } else if (activeTab === "mySubscriptions") {
        apiEndpoint = `${process.env.REACT_APP_API_URL}/my-subscriptions`;
      } else {
        apiEndpoint = `${process.env.REACT_APP_API_URL}/data-discovery-results`;
      }

      const response = await axios.post(apiEndpoint, payload);

      if (response.data && response.data.data !== undefined) {
        setApiData(response.data.data);
        // Clear filteredData when new API data comes in - applyFilters will handle it
        setFilteredData([]);

        // Handle filter options from API response - new hierarchical structure
        if (response.data.filters && response.data.filters.parent_domains) {
          // Store the full hierarchical filters data
          setHierarchicalFilters(response.data.filters);

          // Extract ALL available options for each filter level (initially show everything)
          const parentDomains = response.data.filters.parent_domains.map(
            (pd) => pd.parent_domain
          );

          // Get ALL domains from ALL parent domains
          const allDomains = [];
          response.data.filters.parent_domains.forEach((pd) => {
            pd.domains.forEach((d) => {
              if (!allDomains.includes(d.domain)) {
                allDomains.push(d.domain);
              }
            });
          });

          // Get ALL sub domains from ALL domains
          const allSubDomains = [];
          response.data.filters.parent_domains.forEach((pd) => {
            pd.domains.forEach((d) => {
              d.sub_domains.forEach((sd) => {
                if (!allSubDomains.includes(sd.sub_domain)) {
                  allSubDomains.push(sd.sub_domain);
                }
              });
            });
          });

          // Get ALL products from ALL sub domains
          const allProducts = [];
          response.data.filters.parent_domains.forEach((pd) => {
            pd.domains.forEach((d) => {
              d.sub_domains.forEach((sd) => {
                sd.products.forEach((p) => {
                  if (!allProducts.includes(p)) {
                    allProducts.push(p);
                  }
                });
              });
            });
          });

          // Set ALL options initially
          setFilterOptions({
            parentDomains: parentDomains,
            domains: allDomains,
            subDomains: allSubDomains,
            products: allProducts,
          });
        }

        // Handle pagination data from API response
        if (response.data.pagination) {
          setTotalCount(response.data.pagination.totalCount);
          setTotalPages(response.data.pagination.totalPages);
          setCurrentPage(response.data.pagination.page);
          setPageSize(response.data.pagination.pageSize);
        } else {
          // Fallback pagination if API doesn't provide it
          const dataLength = response.data.data ? response.data.data.length : 0;
          setTotalCount(dataLength);
          setTotalPages(Math.ceil(dataLength / pageSize));
          setCurrentPage(1);
          // Keep existing pageSize
        }

        // Don't set filteredData here - let applyFilters handle it with new data
      } else {
        setApiData([]);
        setFilteredData([]); // Clear filtered data when API returns no data
        setTotalCount(0);
        setTotalPages(0);
        setCurrentPage(1);
      }
    } catch (err) {
      setError("Failed to fetch data. Please try again.");
      setApiData([]);
      setFilteredData([]); // Clear filtered data on error
      setTotalCount(0);
      setTotalPages(0);
      setCurrentPage(1);
    } finally {
      setLoading(false);
      // API call completed - no fallback data needed
    }
  };

  // Fetch data from API when component mounts or when active tab changes
  useEffect(() => {
    setLoading(true);
    fetchDataDiscovery();
  }, [activeTab]);

  // Effect to update filtered data when filters change (local filtering only)
  useEffect(() => {
    // Only apply filters locally when filters change, don't call API
    applyFilters();
  }, [parentDomain, domain, subDomain, product]);

  // Effect to apply current filters when apiData changes (after API calls)
  useEffect(() => {
    if (apiData.length > 0) {
      applyFilters();
    }
  }, [apiData]);

  // Debug effect to monitor loading state changes
  useEffect(() => {}, [searchLoading, filterLoading]);

  const performSearch = async () => {
    // Reset to page 1 when searching
    setCurrentPage(1);

    // Set search loading state
    setSearchLoading(true);

    // Process search query - convert spaces to underscores and trim
    const processedSearchQuery = searchQuery ? searchQuery.trim().replace(/\s+/g, '_') : "";

    const payload = {
      parentDomain: parentDomain === "All Parent Domains" ? "" : parentDomain,
      domain: domain === "All Domains" ? "" : domain,
      subDomain: subDomain === "All Sub Domains" ? "" : subDomain,
      product: product === "All Products" ? "" : product,
      searchValue: processedSearchQuery,
      page: 1, // Always start from page 1 when searching
      pageSize: pageSize,
    };

    try {
      await fetchDataDiscovery(payload);
    } catch (error) {
    } finally {
      // Clear search loading state
      setSearchLoading(false);
    }
  };

  // Handle search submission (Enter key)
  const handleSearchSubmit = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (searchQuery.trim()) {
        performSearch();
      } else {
        // If search is empty, fetch all data with current filters
        setSearchLoading(true);
        fetchDataDiscovery().finally(() => {
          setSearchLoading(false);
        });
      }
    }
  };

  // Handle page change - make API call for pagination
  const handlePageChange = async (newPage) => {
    setCurrentPage(newPage);

    // Process search query - convert spaces to underscores and trim
    const processedSearchQuery = searchQuery ? searchQuery.trim().replace(/\s+/g, '_') : "";

    const payload = {
      parentDomain: parentDomain === "All Parent Domains" ? "" : parentDomain,
      domain: domain === "All Domains" ? "" : domain,
      subDomain: subDomain === "All Sub Domains" ? "" : subDomain,
      product: product === "All Products" ? "" : product,
      searchValue: processedSearchQuery,
      page: newPage,
      pageSize: pageSize,
    };

    await fetchDataDiscovery(payload);
  };

  // Handle parent domain change - cascade to other filters
  const handleParentDomainChange = (event) => {
    const newParentDomain = event.target.value;
    setParentDomain(newParentDomain);

    // Reset other filters when parent domain changes
    if (newParentDomain === "All Parent Domains") {
      setDomain("All Domains");
      setSubDomain("All Sub Domains");
      setProduct("All Products");
      setParentDomainId(null);
      setDomainId(null);
      setSubDomainId(null);
    } else {
      // Find related domains, sub domains, and products for this parent domain
      if (hierarchicalFilters && hierarchicalFilters.parent_domains) {
        const selectedParentDomain = hierarchicalFilters.parent_domains.find(
          (pd) => pd.parent_domain === newParentDomain
        );

        if (selectedParentDomain) {
          setParentDomainId(selectedParentDomain.parent_domain_id);

          const relatedDomains = selectedParentDomain.domains.map(
            (d) => d.domain
          );

          // Auto-select domain if only one option available
          if (relatedDomains.length === 1) {
            setDomain(relatedDomains[0]);
            setDomainId(selectedParentDomain.domains[0].domain_id);

            // Also auto-select sub domain and product if only one option
            const selectedDomain = selectedParentDomain.domains[0];
            const domainSubDomains = selectedDomain.sub_domains.map(
              (sd) => sd.sub_domain
            );

            if (domainSubDomains.length === 1) {
              setSubDomain(domainSubDomains[0]);
              setSubDomainId(selectedDomain.sub_domains[0].sub_domain_id);

              const selectedSubDomain = selectedDomain.sub_domains[0];
              const subDomainProducts = selectedSubDomain.products;

              if (subDomainProducts.length === 1) {
                setProduct(subDomainProducts[0]);
              } else {
                setProduct("All Products");
              }
            } else {
              setSubDomain("All Sub Domains");
              setSubDomainId(null);
              setProduct("All Products");
            }
          } else {
            setDomain("All Domains");
            setDomainId(null);
            setSubDomain("All Sub Domains");
            setSubDomainId(null);
            setProduct("All Products");
          }
        }
      }
    }

    // Reset to page 1 when filters change
    setCurrentPage(1);

    // Make API call with new filter - show loading in contracts list area
    setFilterLoading(true);

    // Determine the actual values to send in payload (including auto-selected ones)
    let payloadDomain = domain;
    let payloadSubDomain = subDomain;
    let payloadProduct = product;

    // If we auto-selected domain, use that value
    if (newParentDomain !== "All Parent Domains" && hierarchicalFilters) {
      const selectedParentDomain = hierarchicalFilters.parent_domains.find(
        (pd) => pd.parent_domain === newParentDomain
      );
      if (selectedParentDomain && selectedParentDomain.domains.length === 1) {
        payloadDomain = selectedParentDomain.domains[0].domain;

        // If we auto-selected sub domain, use that value too
        if (selectedParentDomain.domains[0].sub_domains.length === 1) {
          payloadSubDomain =
            selectedParentDomain.domains[0].sub_domains[0].sub_domain;

          // If we auto-selected product, use that value too
          if (
            selectedParentDomain.domains[0].sub_domains[0].products.length === 1
          ) {
            payloadProduct =
              selectedParentDomain.domains[0].sub_domains[0].products[0];
          }
        }
      }
    }

    // Process search query - convert spaces to underscores and trim
    const processedSearchQuery = searchQuery ? searchQuery.trim().replace(/\s+/g, '_') : "";

    // Create payload with the actual values (including auto-selected ones)
    const payload = {
      parentDomain:
        newParentDomain === "All Parent Domains" ? "" : newParentDomain,
      domain: payloadDomain === "All Domains" ? "" : payloadDomain,
      subDomain: payloadSubDomain === "All Sub Domains" ? "" : payloadSubDomain,
      product: payloadProduct === "All Products" ? "" : payloadProduct,
      searchValue: processedSearchQuery,
      page: 1,
      pageSize: pageSize,
    };

    // Call API with the new payload
    fetchDataDiscovery(payload).finally(() => {
      setFilterLoading(false);
    });
  };

  // Handle domain change - cascade to sub domain and product
  const handleDomainChange = (event) => {
    const newDomain = event.target.value;
    setDomain(newDomain);

    if (newDomain === "All Domains") {
      setSubDomain("All Sub Domains");
      setProduct("All Products");
      setDomainId(null);
      setSubDomainId(null);
    } else {
      // Find related sub domains and products for this domain
      if (hierarchicalFilters && hierarchicalFilters.parent_domains) {
        const selectedParentDomain = hierarchicalFilters.parent_domains.find(
          (pd) => pd.parent_domain === parentDomain
        );

        if (selectedParentDomain) {
          const selectedDomain = selectedParentDomain.domains.find(
            (d) => d.domain === newDomain
          );

          if (selectedDomain) {
            setDomainId(selectedDomain.domain_id);
            const relatedSubDomains = selectedDomain.sub_domains.map(
              (sd) => sd.sub_domain
            );

            // Auto-select sub domain if only one option available
            if (relatedSubDomains.length === 1) {
              setSubDomain(relatedSubDomains[0]);
              setSubDomainId(selectedDomain.sub_domains[0].sub_domain_id);

              // Also auto-select product if only one option
              const selectedSubDomain = selectedDomain.sub_domains[0];
              const subDomainProducts = selectedSubDomain.products;

              if (subDomainProducts.length === 1) {
                setProduct(subDomainProducts[0]);
              } else {
                setProduct("All Products");
              }
            } else {
              setSubDomain("All Sub Domains");
              setSubDomainId(null);
              setProduct("All Products");
            }
          }
        }
      }
    }

    // Reset to page 1 when filters change
    setCurrentPage(1);

    // Make API call with new filter - show loading in contracts list area
    setFilterLoading(true);

    // Determine the actual values to send in payload (including auto-selected ones)
    let payloadSubDomain = subDomain;
    let payloadProduct = product;

    // If we auto-selected sub domain, use that value
    if (newDomain !== "All Domains" && hierarchicalFilters) {
      const selectedParentDomain = hierarchicalFilters.parent_domains.find(
        (pd) => pd.parent_domain === parentDomain
      );
      if (selectedParentDomain) {
        const selectedDomain = selectedParentDomain.domains.find(
          (d) => d.domain === newDomain
        );
        if (selectedDomain && selectedDomain.sub_domains.length === 1) {
          payloadSubDomain = selectedDomain.sub_domains[0].sub_domain;

          // If we auto-selected product, use that value too
          if (selectedDomain.sub_domains[0].products.length === 1) {
            payloadProduct = selectedDomain.sub_domains[0].products[0];
          }
        }
      }
    }

    // Process search query - convert spaces to underscores and trim
    const processedSearchQuery = searchQuery ? searchQuery.trim().replace(/\s+/g, '_') : "";

    // Create payload with the actual values (including auto-selected ones)
    const payload = {
      parentDomain: parentDomain === "All Parent Domains" ? "" : parentDomain,
      domain: newDomain === "All Domains" ? "" : newDomain,
      subDomain: payloadSubDomain === "All Sub Domains" ? "" : payloadSubDomain,
      product: payloadProduct === "All Products" ? "" : payloadProduct,
      searchValue: processedSearchQuery,
      page: 1,
      pageSize: pageSize,
    };

    // Call API with the new payload
    fetchDataDiscovery(payload).finally(() => {
      setFilterLoading(false);
    });
  };

  // Handle sub domain change - cascade to product
  const handleSubDomainChange = (event) => {
    const newSubDomain = event.target.value;
    setSubDomain(newSubDomain);

    if (newSubDomain === "All Sub Domains") {
      setProduct("All Products");
      setSubDomainId(null);
    } else {
      // Find related products for this sub domain
      if (hierarchicalFilters && hierarchicalFilters.parent_domains) {
        const selectedParentDomain = hierarchicalFilters.parent_domains.find(
          (pd) => pd.parent_domain === parentDomain
        );

        if (selectedParentDomain) {
          const selectedDomain = selectedParentDomain.domains.find(
            (d) => d.domain === domain
          );

          if (selectedDomain) {
            const selectedSubDomain = selectedDomain.sub_domains.find(
              (sd) => sd.sub_domain === newSubDomain
            );

            if (selectedSubDomain) {
              setSubDomainId(selectedSubDomain.sub_domain_id);
              const relatedProducts = selectedSubDomain.products;

              // Auto-select product if only one option available
              if (relatedProducts.length === 1) {
                setProduct(relatedProducts[0]);
              } else {
                setProduct("All Products");
              }
            }
          }
        }
      }
    }

    // Reset to page 1 when filters change
    setCurrentPage(1);

    // Make API call with new filter - show loading in contracts list area
    setFilterLoading(true);

    // Determine the actual values to send in payload (including auto-selected ones)
    let payloadProduct = product;

    // If we auto-selected product, use that value
    if (newSubDomain !== "All Sub Domains" && hierarchicalFilters) {
      const selectedParentDomain = hierarchicalFilters.parent_domains.find(
        (pd) => pd.parent_domain === parentDomain
      );
      if (selectedParentDomain) {
        const selectedDomain = selectedParentDomain.domains.find(
          (d) => d.domain === domain
        );
        if (selectedDomain) {
          const selectedSubDomain = selectedDomain.sub_domains.find(
            (sd) => sd.sub_domain === newSubDomain
          );
          if (selectedSubDomain && selectedSubDomain.products.length === 1) {
            payloadProduct = selectedSubDomain.products[0];
          }
        }
      }
    }

    // Process search query - convert spaces to underscores and trim
    const processedSearchQuery = searchQuery ? searchQuery.trim().replace(/\s+/g, '_') : "";

    // Create payload with the actual values (including auto-selected ones)
    const payload = {
      parentDomain: parentDomain === "All Parent Domains" ? "" : parentDomain,
      domain: domain === "All Domains" ? "" : domain,
      subDomain: newSubDomain === "All Sub Domains" ? "" : newSubDomain,
      product: payloadProduct === "All Products" ? "" : payloadProduct,
      searchValue: processedSearchQuery,
      page: 1,
      pageSize: pageSize,
    };

    // Call API with the new payload
    fetchDataDiscovery(payload).finally(() => {
      setFilterLoading(false);
    });
  };

  // Handle product change
  const handleProductChange = (event) => {
    const newProduct = event.target.value;
    setProduct(newProduct);

    // Reset to page 1 when filters change
    setCurrentPage(1);

    // Make API call with new filter - show loading in contracts list area
    setFilterLoading(true);

    // Process search query - convert spaces to underscores and trim
    const processedSearchQuery = searchQuery ? searchQuery.trim().replace(/\s+/g, '_') : "";

    // Create payload with the new value directly instead of waiting for state update
    const payload = {
      parentDomain: parentDomain === "All Parent Domains" ? "" : parentDomain,
      domain: domain === "All Domains" ? "" : domain,
      subDomain: subDomain === "All Sub Domains" ? "" : subDomain,
      product: newProduct === "All Products" ? "" : newProduct,
      searchValue: processedSearchQuery,
      page: 1,
      pageSize: pageSize,
    };

    // Call API with the new payload
    fetchDataDiscovery(payload).finally(() => {
      setFilterLoading(false);
    });
  };

  // Transform API data to match the expected contract format
  const transformApiDataToContracts = (data) => {
    return data.map((item, index) => ({
      id: item.data_asset_id || `contract-${index}`,
      name: item.data_asset_name || "Unnamed Asset",
      version: item?.version || "1.0.0", // Default version since API doesn't provide it
      type: "INTERNAL", // Default type
      status: "ACTIVE", // Default status
      owner: item.created_by || "Unknown Owner",
      lastUpdated: item.last_modified
        ? new Date(item.last_modified).toLocaleDateString()
        : "Unknown",
      tables: [item.data_asset_name], // Use data asset name as table
      qualityScore: item.quality_score || 0,
      description: item.data_assets_description || "No description available",
      tablesCount: 1, // Each item represents one data asset
      assets: [item.data_asset_name], // Use data asset name as asset

      // Additional fields from API
      executionDate: item.execution_date,
      productName: item.product_name,
      productOwner: item.product_owner,
      dataLayer: item.data_layer,
      parentDomainName: item.parent_domain_name,
      domainName: item.domain_name,
      subDomainName: item.sub_domain_name,
      dqRuleCount: item.dq_rule_count,
      dqTrend: item.trend || item.dq_trend || "N/A", // Handle both trend and dq_trend
      issues: item.issues,
      createdDate: item.created_date,
      profileStatus: item.profileStatus || "N/A",

      // Product ID for API calls
      productId: item.product_id || null,
    }));
  };

  const handleSubscribe = (contract) => {
    if (subscribedTables.includes(contract.id)) {
      setSubscribedTables((prev) => prev.filter((id) => id !== contract.id));
    } else {
      setSelectedTable(contract);
      setShowSubscriptionForm(true);
    }
  };

  const closeSubsciptionModal = () => {
    setFormTouched(false);
    setSubscribedTables((prev) => [...prev, selectedTable.id]);
    setShowSubscriptionForm(false);
    setSelectedTable(null);
    setSubscriptionForm({
      productName: "",
      projectCode: "",
      readerGroup: "",
      usageDuration: "3months",
      usageDescription: "",
      notificationEmail: "",
      acceptTerms: false,
    });
  };

  const handleSubscriptionChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setSubscriptionForm((prev) => ({ ...prev, [name]: newValue }));
    
    // Real-time validation for email field
    if (name === 'notificationEmail' && value) {
      const emailRegex = /^[^\s@]+@effem\.com$/;
      if (!emailRegex.test(value)) {
        setFormErrors(prev => ({
          ...prev,
          notificationEmail: "Only @aaysinsight.com email addresses are allowed"
        }));
      } else {
        setFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.notificationEmail;
          return newErrors;
        });
      }
    }
  };

  const validateConfigForm = () => {
    const errors = {};
    if (!subscriptionForm.productName) errors.productName = "Required";
    if (!subscriptionForm.projectCode) errors.projectCode = "Required";
    if (!subscriptionForm.readerGroup) errors.readerGroup = "Required";
    if (!subscriptionForm.usageDuration) errors.usageDuration = "Required";
    if (!subscriptionForm.usageDescription)
      errors.usageDescription = "Required";
    if (!subscriptionForm.notificationEmail) {
      errors.notificationEmail = "Required";
    } else {
      // Email validation - only allow @aaysinsight.com domain
      const emailRegex = /^[^\s@]+@effem\.com$/;
      if (!emailRegex.test(subscriptionForm.notificationEmail)) {
        errors.notificationEmail = "Only @aaysinsight.com email addresses are allowed";
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubscriptionSubmit = () => {
    setFormTouched(true);
    // Now do full validation (including email format)
    if (!validateConfigForm()) return;

    const requestDetails = {
      requestedBy: localStorage.getItem("userId"),
      usageDuration: subscriptionForm.usageDuration,
      usageDescription: subscriptionForm.usageDescription,
      readerGroup: subscriptionForm.readerGroup,
      productName: subscriptionForm.productName,
      projectCode: subscriptionForm.projectCode,
      notificationEmail: subscriptionForm.notificationEmail,
    };

    const requestPayload = {
      dataAssetId: selectedTable.id,
      requestDetails: requestDetails,
    };

    try {
      const response = axios.post(
        `${process.env.REACT_APP_API_URL}/subscription-request`,
        requestPayload
      );

      if (response.status === 200 || 201) {
        if (selectedTable) {
          closeSubsciptionModal();
        }
      }

      toast.success("Subscription to the asset configured successfully!");
    } catch (error) {
      toast.error("Failed to subscribe to the asset. Please try again later.");
    }
  };

  const handleAlertPreferenceChange = (preference) => {
    setSubscriptionForm((prev) => ({
      ...prev,
      alertPreferences: {
        ...prev.alertPreferences,
        [preference]: !prev.alertPreferences[preference],
      },
    }));
  };

  const handleViewProfile = (contract) => {
    navigate(`/onboarding/data-profiling/${contract.tables.join(",")}`);
  };

  const handleViewContract = async (contract) => {
    if (contract.id) {
      setSelectedContractForView(contract);
      setShowContractModal(true);
      
      // Fetch contract details from API
      await fetchContractDetails(contract.id);
    }
  };

  // Fetch contract details from API
  const fetchContractDetails = async (dataAssetId) => {
    if (!dataAssetId) {
      return null;
    }

    try {
      setContractLoading(true);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/view-contracts/${dataAssetId}`
      );

      if (response.data && response.data.status === "success") {
        setContractDetails(response.data.data);
        return response.data.data;
      } else {
        setContractDetails(null);
        return null;
      }
    } catch (err) {
      console.error("Error fetching contract details:", err);
      setContractDetails(null);
      return null;
    } finally {
      setContractLoading(false);
    }
  };

  const handleViewProduct = async (contract) => {
    if (contract.productId) {
      // Check if we already have this product's details
      const existingProduct = productDetails.find(
        (p) => p.productId === contract.productId
      );

      if (existingProduct) {
        // Open product details popup with existing data
        setSelectedProductForView(existingProduct);
        setShowProductModal(true);
      } else {
        setProductLoading(true); // Start loading for product details
        const productData = await fetchProductDetails(contract.productId);

        if (productData) {
          // Open product details popup with fetched data
          setSelectedProductForView(productData);
          setShowProductModal(true);
        } else {
          // TODO: Show error message to user
        }
      }
    } else {
      // TODO: Show error message to user
    }
  };

  const handleViewAssetDetails = async (contract) => {
    if (contract.id) {
      // Check if we already have this asset's details
      const existingAsset =
        assetData?.data_asset_id === contract.id ? assetData : null;

      if (existingAsset) {
        setSelectedAssetForView(contract);
        setShowAssetModal(true);
      } else {
        const assetDetails = await fetchAssetDetails(contract.id);

        if (assetDetails) {
          setSelectedAssetForView(contract);
          setAssetData(assetDetails);
          setShowAssetModal(true);
        } else {
          // TODO: Show error message to user
        }
      }
    } else {
      // TODO: Show error message to user
    }
  };

  const handleViewResult = async (contract) => {
    // Open result details popup
    setSelectedResultForView(contract);
    setShowResultModal(true);
    setActiveResultTab("profile"); // Default to profile tab

    // Fetch profiling results for this contract
    if (contract.productId) {
      await fetchProfilingResults(contract.productId);

      // Also fetch DQ results for the quality tab
      if (contract.id) {
        await fetchDQResults(contract.productId, contract.id);
      }
    } else {
    }
  };

  const handleViewLineage = (contract) => {
    // Open lineage modal with contract details
    setSelectedLineageForView(contract);
    setShowLineageModal(true);
  };

  // Fetch product details from edit-form API
  const fetchProductDetails = async (productId) => {
    if (!productId) {
      return null;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/edit-form/${productId}`
      );

      if (response.data && response.data.productId) {
        // Add to productDetails array if not already present
        setProductDetails((prev) => {
          const existing = prev.find((p) => p.productId === productId);
          if (!existing) {
            return [...prev, response.data];
          }
          return prev;
        });

        return response.data;
      } else {
        return null;
      }
    } catch (err) {
      return null;
    } finally {
      setProductLoading(false); // End loading for product details
    }
  };

  // Fetch profiling results from API
  const fetchProfilingResults = async (productId) => {
    if (!productId) {
      return null;
    }

    try {
      setProfilingLoading(true);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/profiling_results/${productId}`
      );

      if (response.data && response.data.profiling_results) {
        setProfilingResults(response.data);
        return response.data;
      } else {
        return null;
      }
    } catch (err) {
      return null;
    } finally {
      setProfilingLoading(false);
    }
  };

  // Fetch DQ results from API
  const fetchDQResults = async (productId, dataAssetId) => {
    if (!productId || !dataAssetId) {
      return null;
    }

    try {
      setDqLoading(true);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/runQuality/ruleStatus/${productId}`
      );

      if (response.data && Array.isArray(response.data)) {
        // Find the asset with matching data_asset_id
        const matchingAsset = response.data.find(
          (asset) => asset.data_asset_id === dataAssetId
        );

        if (matchingAsset) {
          setDqResults(matchingAsset);
          return matchingAsset;
        } else {
          setDqResults(null);
          return null;
        }
      } else {
        return null;
      }
    } catch (err) {
      return null;
    } finally {
      setDqLoading(false);
    }
  };

  // Fetch asset details from API
  const fetchAssetDetails = async (assetId) => {
    if (!assetId) {
      return null;
    }

    try {
      setAssetLoading(true);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/assets/${assetId}`
      );

      if (response.data) {
        setAssetData(response.data);
        return response.data;
      } else {
        return null;
      }
    } catch (err) {
      return null;
    } finally {
      setAssetLoading(false);
    }
  };

  const handleAssetSelectionChange = (assetIds) => {
    setSelectedAssets(assetIds);
  };

  // Memoized filtered contracts - no client-side pagination needed
  const getFilteredContracts = () => {
    // Ensure filteredData exists and is an array
    if (!filteredData || !Array.isArray(filteredData)) {
      return [];
    }
    
    let contracts = transformApiDataToContracts(filteredData);

    // TEMPORARILY DISABLED: Filter by selected assets
    // if (selectedAssets.length > 0) {
    //   contracts = contracts.filter(contract =>
    //     contract.assets.some(asset => selectedAssets.includes(asset))
    //   );
    // }

    return contracts;
  };

  const filteredContracts = getFilteredContracts();

  // Use the same filtered contracts for all tabs
  const myContracts = filteredContracts; // Use API data based on activeTab
  const mySubscriptions = filteredContracts; // Use API data based on activeTab

  // Get unique values for filter dropdowns based on current filter context - updated for hierarchical structure
  const getFilterOptions = useMemo(() => {
    // Start with ALL available options initially
    let availableOptions = { ...filterOptions };

    // Use the hierarchical filters data from API for cascading logic
    if (hierarchicalFilters && hierarchicalFilters.parent_domains) {
      // Apply cascading logic to filter options based on current selections
      if (parentDomain !== "All Parent Domains") {
        // Find the selected parent domain
        const selectedParentDomain = hierarchicalFilters.parent_domains.find(
          (pd) => pd.parent_domain === parentDomain
        );

        if (selectedParentDomain) {
          // Get ONLY domains for this parent domain
          const domains = selectedParentDomain.domains.map((d) => d.domain);
          availableOptions.domains = domains;

          // If domain is selected, get ONLY sub domains for this domain
          if (domain !== "All Domains") {
            const selectedDomain = selectedParentDomain.domains.find(
              (d) => d.domain === domain
            );
            if (selectedDomain) {
              const subDomains = selectedDomain.sub_domains.map(
                (sd) => sd.sub_domain
              );
              availableOptions.subDomains = subDomains;

              // If sub domain is selected, get ONLY products for this sub domain
              if (subDomain !== "All Sub Domains") {
                const selectedSubDomain = selectedDomain.sub_domains.find(
                  (sd) => sd.sub_domain === subDomain
                );
                if (selectedSubDomain) {
                  availableOptions.products = selectedSubDomain.products;
                }
              }
            }
          }
        }
      }
    }

    return availableOptions;
  }, [hierarchicalFilters]);

  // Handle tab change - reset pagination
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setCurrentPage(1); // Reset to page 1 when switching tabs
  };

  // Reset all filters to default values
  const handleResetFilters = () => {
    setParentDomain("All Parent Domains");
    setParentDomainId(null);
    setDomain("All Domains");
    setDomainId(null);
    setSubDomain("All Sub Domains");
    setSubDomainId(null);
    setProduct("All Products");
    setSearchQuery("");
    setCurrentPage(1);
    // Fetch data with reset filters (will use correct API based on activeTab)
    fetchDataDiscovery();
  };

  if (loading) {
    return (
    <div className="relative bg-white flex items-center justify-center z-50 min-h-screen">
  <div className="flex flex-col items-center space-y-3">
    <img src={preloader} alt="Loading..." width={200} />
    <span className="text-gray-700 text-[20px] text-center">
      Hang tight! The resources are loading...
    </span>
  </div>
</div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => {
              setError(null);
              fetchDataDiscovery();
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ width: "100%" }}>
           <div className="font-bold font-family-poppins pt-2  text-[24px] text-[#424242]">Data Discovery</div>
        <p className="pb-6">Discover and analyze datasets linked to parent domains, subdomains, and products.</p>
        <div className="card">
          {/* <div className="card-header flex justify-between items-center">
            <div>
              <h1 className="card-title">Data Contract</h1>
              <p className="card-subtitle">
                Browse and manage your data contracts
              </p>
            </div>

            <div className="flex items-center gap-3">
              <div className="text-sm text-gray-500">
                {selectedAssets.length > 0 ? (
                  <>
                    Selected Assets:
                    <span className="font-medium text-gray-900 ml-1">
                      {selectedAssets
                        .map((asset) => asset.toUpperCase())
                        .join(", ")}
                    </span>
                  </>
                ) : (
                  <span className="text-gray-400">No assets selected</span>
                )}
              </div>
            </div>
          </div> */}

          <ContractTabs activeTab={activeTab} setActiveTab={handleTabChange} />

          <div className="card-body">
            <SearchAndFilter
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              showFilters={showFilters}
              setShowFilters={setShowFilters}
              parentDomain={parentDomain}
              setParentDomain={handleParentDomainChange}
              domain={domain}
              setDomain={handleDomainChange}
              subDomain={subDomain}
              setSubDomain={handleSubDomainChange}
              product={product}
              setProduct={handleProductChange}
              filterOptions={getFilterOptions}
              handleResetFilters={handleResetFilters}
              handleParentDomainChange={handleParentDomainChange}
              handleDomainChange={handleDomainChange}
              handleSubDomainChange={handleSubDomainChange}
              handleProductChange={handleProductChange}
              handleSearchSubmit={handleSearchSubmit}
            />

            {/* Loading State Indicator for Debug */}
            {/* {(searchLoading || filterLoading) && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center space-x-2">
                <img src={heartLoading} alt="Loading..." className="h-30" />
                  <span className="text-blue-800 font-medium">
                    {searchLoading ? 'Searching...' : 'Applying filters...'}
                  </span>
                </div>
              </div>
            )} */}

            <ContractList
              activeTab={activeTab}
              filteredContracts={filteredContracts}
              mySubscriptions={mySubscriptions}
              myContracts={myContracts}
              subscribedTables={subscribedTables}
              handleSubscribe={handleSubscribe}
              handleViewProfile={handleViewProfile}
              handleViewContract={handleViewContract}
              handleViewProduct={handleViewProduct}
              handleViewAssetDetails={handleViewAssetDetails}
              handleViewResult={handleViewResult}
              handleViewLineage={handleViewLineage}
              // Pagination props
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              // Loading states
              loading={loading}
              searchLoading={searchLoading}
              filterLoading={filterLoading}
            />
          </div>
        </div>

        <Toaster position="top-right" />
        {showSubscriptionForm && selectedTable && (
          <SubscriptionModal
            selectedAsset={selectedTable}
            subscriptionForm={subscriptionForm}
            closeSubsciptionModal={closeSubsciptionModal}
            handleSubscriptionSubmit={handleSubscriptionSubmit}
            handleSubscriptionChange={handleSubscriptionChange}
            setShowSubscriptionForm={setShowSubscriptionForm}
            showSubscriptionForm={showSubscriptionForm}
            allFieldsValid={allFieldsValid}
            formTouched={formTouched}
            formErrors={formErrors}
            products={getFilterOptions?.products}
          />
        )}

        {showContractModal && selectedContractForView && (
          <ContractDetailModal
            selectedContractForView={selectedContractForView}
            contractDetails={contractDetails}
            contractLoading={contractLoading}
            setShowContractModal={setShowContractModal}
            onClose={() => {
              setShowContractModal(false);
              setSelectedContractForView(null);
              setContractDetails(null);
            }}
          />
        )}

        {/* Product Details Modal */}
        {showProductModal && selectedProductForView && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className="bg-white rounded-lg shadow-xl w-4/5 max-h-[90vh] overflow-hidden">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">
                    Product Details
                  </h2>
                </div>
                <button
                  onClick={() => {
                    setShowProductModal(false);
                    setSelectedProductForView(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
                  title="Close"
                >
                  <svg
                    className=" h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                {productLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                      <img
                        src={preloader}
                        alt="Loading..."
                        width={200}
                        className="mx-auto mb-4"
                      />
                      <p className="text-gray-600">
                        Loading product details...
                      </p>
                    </div>
                  </div>
                ) : selectedProductForView ? (
                  <>
                    <ProductDetailsView
                      publishData={{
                        ProductInfo: selectedProductForView,
                      }}
                    />
                  </>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-600">
                      No product details available
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Result Details Modal */}
        {showResultModal && selectedResultForView && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className="bg-white rounded-lg shadow-xl w-4/5 h-[90vh] overflow-hidden">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">Results</h2>
                </div>
                <button
                  onClick={() => {
                    setShowResultModal(false);
                    setSelectedResultForView(null);
                    setActiveResultTab("profile");
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
                  title="Close"
                >
                  <svg
                    className="h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200">
                <div className="flex">
                  <button
                    onClick={() => setActiveResultTab("profile")}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeResultTab === "profile"
                        ? "border-[#bb1ccc] text-[#bb1ccc]"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    Data Profile Results
                  </button>
                  <button
                    onClick={() => setActiveResultTab("quality")}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeResultTab === "quality"
                        ? "border-[#bb1ccc] text-[#bb1ccc]"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    Data Quality Results
                  </button>
                  <button
                    disabled={true}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors cursor-not-allowed opacity-50 ${
                      activeResultTab === "remediation"
                        ? "border-[#bb1ccc] text-[#bb1ccc]"
                        : "border-transparent text-gray-400"
                    }`}
                    title="Remediation Action feature is currently disabled"
                  >
                    Remediation Action
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto h-[calc(90vh-180px)]">
                {activeResultTab === "profile" ? (
                  <div>
                    {profilingLoading ? (
                      <div className="flex items-center justify-center py-8 mt-[4rem]">
                        <div className="text-center">
                          <img
                            src={preloader}
                            alt="Loading..."
                            width={200}
                          />
                          <p className="text-gray-600">
                            Loading profiling results...
                          </p>
                        </div>
                      </div>
                    ) : profilingResults ? (
                      <ProfilingResults data={profilingResults} />
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-gray-600">
                          No profiling results available
                        </p>
                      </div>
                    )}
                  </div>
                ) : activeResultTab === "quality" ? (
                  <div>
                    {dqLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="text-center">
                          <img
                            src={preloader}
                            alt="Loading..."
                            width={200}
                            className="mx-auto"
                          />
                          <p className="text-gray-600">
                            Loading data quality results...
                          </p>
                        </div>
                      </div>
                    ) : dqResults ? (
                      <DQResult results={dqResults} />
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-gray-600">
                          No data quality results available
                        </p>
                      </div>
                    )}
                  </div>
                ) : activeResultTab === "remediation" ? (
                  <div className="text-center py-8">
                    <div className="max-w-md mx-auto">
                      <div className="mb-4">
                        <svg
                          className="mx-auto h-16 w-16 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                          />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        Remediation Action
                      </h3>
                      <p className="text-gray-600 mb-4">
                        This feature is currently under development and will be
                        available soon.
                      </p>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-800">
                          <strong>Coming Soon:</strong> Automated remediation
                          actions for data quality issues, including rule
                          suggestions and workflow automation.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-600">No results available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Asset Details Modal */}
        {showAssetModal && selectedAssetForView && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className="bg-white rounded-lg shadow-xl w-4/5 h-[90vh] overflow-hidden">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">
                    Asset Overview
                  </h2>
                </div>
                <button
                  onClick={() => {
                    setShowAssetModal(false);
                    setSelectedAssetForView(null);
                    setAssetData(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
                  title="Close"
                >
                  <svg
                    className="h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto h-[calc(90vh-120px)]">
                {assetLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                      <img
                        src={preloader}
                        alt="Loading..."
                        width={200}
                        className="mx-auto"
                      />
                      <p className="text-gray-600">Loading asset details...</p>
                    </div>
                  </div>
                ) : assetData ? (
                  <AssetDetails
                    assetData={assetData}
                    searchQuery={searchQuery}
                  />
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-600">No asset details available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Lineage Modal */}
        {showLineageModal && selectedLineageForView && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className="bg-white rounded-lg shadow-xl w-[90vw] h-[90vh] overflow-hidden">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">
                    Data Lineage
                  </h2>
                </div>
                <button
                  onClick={() => {
                    setShowLineageModal(false);
                    setSelectedLineageForView(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
                  title="Close"
                >
                  <svg
                    className="h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-[8px] overflow-y-auto h-[calc(90vh-80px)]">
                <div className="flex items-center justify-center h-full">
                  <ViewLineage contractData={selectedLineageForView} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LandingPage;
